/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Pencil, Building2, Users, DollarSign, Database,
  Activity, Shield, CreditCard, Pause, Play, UserPlus, X, Mail, Phone, Loader2,
  Sparkles, ChevronRight, Calendar, Ban, RefreshCw, FileText, Download, Printer
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOrganization, useSuspendOrganization, useReactivateOrganization, useCreateOrgUser, useUpdateOrgUser, useUpdateOrganization, useResendOrgUserInvite, useProvisionRegister, useOrgRoles } from '@/hooks/api/useOrganizations';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { getCountryConfig, getCountryConfigByName, getCountryByName } from '@/data/countries';
import { useSuspendSubscription, useReactivateSubscription, useExtendSubscription, useOrgSubscription, useUpdateGracePeriod } from '@/hooks/api/useSubscriptions';
import { OrganizationDetail, OrganizationAdmin, OrgRoleItem, CreateOrgUserRequest, UpdateOrgUserRequest, UpdateOrganizationRequest } from '@/types/superadmin.types';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { formatCurrency } from '@/lib/utils';
import { useNotification } from '@/hooks/useNotification';
import { useTheme } from '@/hooks';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';
import OrganizationSettingsTab from './OrganizationSettingsTab';
import PremiumTemplatesCard from './components/PremiumTemplatesCard';
import TenantDataCard from './components/TenantDataCard';
import { PaymentAlertBanner } from '@/components/billing/PaymentAlertBanner';
import { InvoicePreviewModal, PrintInvoiceDialog } from '@/components/billing';
import { useOrganizationInvoices, useDownloadInvoicePdfVariant } from '@/hooks/api/useInvoices';

// Stat Card Component
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string | number;
  label: string;
  color: 'green' | 'blue' | 'purple' | 'orange';
  secondaryValue?: string;
  secondaryLabel?: string;
}> = ({ icon, value, label, color, secondaryValue, secondaryLabel }) => {
  const colorClasses = {
    green: 'bg-emerald-500/15 text-emerald-500',
    blue: 'bg-blue-500/15 text-blue-500',
    purple: 'bg-purple-500/15 text-purple-500',
    orange: 'bg-orange-500/15 text-orange-500',
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-4">
      <div className="mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
      <div className="font-mono text-3xl font-bold text-foreground">{value}</div>
      <div className="text-sm text-secondary">{label}</div>
      {secondaryValue && (
        <div className="mt-2 pt-2 border-t border-border/50">
          <div className="font-mono text-lg font-semibold text-foreground/80">{secondaryValue}</div>
          {secondaryLabel && <div className="text-xs text-secondary">{secondaryLabel}</div>}
        </div>
      )}
    </div>
  );
};

// Detail Item Component
const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-secondary text-sm">{label}</span>
    <span className="text-foreground text-base font-medium">{value}</span>
  </div>
);

// Status Badge Component
const StatusBadge: React.FC<{ status: string; t: (key: string) => string }> = ({ status, t }) => {
  const styles: Record<string, string> = {
    ACTIVE: 'bg-emerald-500/15 text-emerald-500',
    TRIAL: 'bg-blue-500/15 text-blue-500',
    PAST_DUE: 'bg-red-500/15 text-red-500',
    EXPIRED: 'bg-orange-500/15 text-orange-500',
    SUSPENDED: 'bg-red-500/15 text-red-500',
    CANCELLED: 'bg-gray-500/15 text-gray-500',
    PENDING_APPROVAL: 'bg-amber-500/15 text-amber-500',
  };

  const labelKeys: Record<string, string> = {
    ACTIVE: 'organizations.status.active',
    TRIAL: 'organizations.status.trial',
    PAST_DUE: 'organizations.status.pastDue',
    EXPIRED: 'organizations.status.expired',
    SUSPENDED: 'organizations.status.suspended',
    CANCELLED: 'organizations.status.cancelled',
    PENDING_APPROVAL: 'organizations.status.pending',
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-500/15 text-gray-500'}`}>
      {t(labelKeys[status] || status)}
    </span>
  );
};

// Plan Badge Component
const PlanBadge: React.FC<{ tier: string }> = ({ tier }) => {
  const styles: Record<string, string> = {
    STARTER: 'bg-gray-500/15 text-gray-400',
    PROFESSIONAL: 'bg-purple-500/15 text-purple-500',
    ENTERPRISE: 'bg-red-500/15 text-red-500',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${styles[tier] || 'bg-gray-500/15 text-gray-400'}`}>
      {tier}
    </span>
  );
};

// Create User Modal Component
interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOrgUserRequest) => void;
  isLoading: boolean;
  roles: OrgRoleItem[];
  orgCountry?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose, onSubmit, isLoading, roles, orgCountry, t }) => {
  const createUserCountryConfig = getCountryConfig(orgCountry || 'GN');
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [formData, setFormData] = useState<CreateOrgUserRequest>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    roleId: undefined,
    sendInvite: true,
  });
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsExiting(false);
      const defaultRoleId = roles.length > 0 ? roles[roles.length - 1].id : undefined;
      setFormData({ firstName: '', lastName: '', email: '', phone: '', roleId: defaultRoleId, sendInvite: true });
      setTimeout(() => firstInputRef.current?.focus(), 100);
    } else {
      setIsExiting(true);
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  if (!isVisible) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) return;
    onSubmit(formData);
  };

  const updateField = (field: keyof CreateOrgUserRequest, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div className={`create-user-backdrop ${isExiting ? 'exiting' : ''}`} onClick={() => !isLoading && onClose()} />
      <div className={`create-user-modal ${isExiting ? 'exiting' : ''}`}>
        <div className="create-user-header">
          <div className="create-user-header-content">
            <div className="create-user-icon">
              <UserPlus size={20} />
            </div>
            <div>
              <h2 className="create-user-title">{t('organizationDetail.createUser.title')}</h2>
              <p className="create-user-subtitle">{t('organizationDetail.createUser.subtitle')}</p>
            </div>
          </div>
          <button className="create-user-close" onClick={onClose} disabled={isLoading}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="create-user-form">
          <div className="form-row">
            <div className="form-group">
              <label>{t('organizationDetail.createUser.fields.firstName')}</label>
              <input
                ref={firstInputRef}
                type="text"
                value={formData.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                placeholder={t('organizationDetail.createUser.placeholders.firstName')}
                disabled={isLoading}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('organizationDetail.createUser.fields.lastName')}</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                placeholder={t('organizationDetail.createUser.placeholders.lastName')}
                disabled={isLoading}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>
              <Mail size={14} />
              {t('organizationDetail.createUser.fields.email')}
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder={t('organizationDetail.createUser.placeholders.email')}
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <PhoneInput
              label={t('organizationDetail.createUser.fields.phone')}
              mask={createUserCountryConfig.phonePlaceholder}
              countryCode={orgCountry || 'GN'}
              value={formData.phone || ''}
              onChange={(v) => updateField('phone', v)}
              name="phone"
            />
          </div>

          <div className="form-group">
            <label>{t('organizationDetail.createUser.fields.role')}</label>
            <select
              value={formData.roleId ?? ''}
              onChange={(e) => updateField('roleId', e.target.value)}
              disabled={isLoading || roles.length === 0}
            >
              {roles.length === 0 && (
                <option value="">{t('organizationDetail.createUser.roles.noRoles')}</option>
              )}
              {roles.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          <div className="form-checkbox">
            <input
              type="checkbox"
              id="sendInvite"
              checked={formData.sendInvite}
              onChange={(e) => updateField('sendInvite', e.target.checked)}
              disabled={isLoading}
            />
            <label htmlFor="sendInvite">{t('organizationDetail.createUser.sendInvite')}</label>
          </div>

          <div className="create-user-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={isLoading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-create" disabled={isLoading || !formData.firstName || !formData.lastName || !formData.email}>
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  {t('organizationDetail.createUser.creating')}
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  {t('organizationDetail.createUser.create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      <style>{createUserModalStyles}</style>
    </>
  );
};

// Edit Organization Details Modal Component
interface EditOrgDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateOrganizationRequest) => void;
  isLoading: boolean;
  initialData: OrganizationDetail | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

const EditOrgDetailsModal: React.FC<EditOrgDetailsModalProps> = ({ isOpen, onClose, onSubmit, isLoading, initialData, t }) => {
  const editOrgCountryConfig = getCountryConfigByName(initialData?.country || '');
  const editOrgCountryCode = getCountryByName(initialData?.country || '')?.code || 'GN';
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [formData, setFormData] = useState<UpdateOrganizationRequest>({
    name: '',
    legalName: '',
    taxId: '',
    rccm: '',
    industry: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
  });
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && initialData) {
      setIsVisible(true);
      setIsExiting(false);
      setFormData({
        name: initialData.name || '',
        legalName: initialData.legalName || '',
        taxId: initialData.taxId || '',
        rccm: initialData.rccm || '',
        industry: initialData.industry || '',
        primaryContactEmail: initialData.primaryContactEmail || '',
        primaryContactPhone: initialData.primaryContactPhone || '',
      });
      setTimeout(() => firstInputRef.current?.focus(), 100);
    } else if (!isOpen) {
      setIsExiting(true);
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  if (!isVisible) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name?.trim()) return;
    onSubmit(formData);
  };

  const updateField = (field: keyof UpdateOrganizationRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const industries = [
    { value: 'RETAIL', label: t('createOrganization.industry.retail') },
    { value: 'IMPORT_EXPORT', label: t('createOrganization.industry.importExport') },
    { value: 'MANUFACTURING', label: t('createOrganization.industry.manufacturing') },
    { value: 'TECHNOLOGY', label: t('createOrganization.industry.technology') },
    { value: 'SERVICES', label: t('createOrganization.industry.services') },
    { value: 'FINANCE', label: t('createOrganization.industry.finance') },
    { value: 'HEALTHCARE', label: t('createOrganization.industry.healthcare') },
    { value: 'EDUCATION', label: t('createOrganization.industry.education') },
    { value: 'OTHER', label: t('createOrganization.industry.other') },
  ];

  return (
    <>
      <div className={`edit-org-backdrop ${isExiting ? 'exiting' : ''}`} onClick={() => !isLoading && onClose()} />
      <div className={`edit-org-modal ${isExiting ? 'exiting' : ''}`}>
        <div className="edit-org-header">
          <div className="edit-org-header-content">
            <div className="edit-org-icon">
              <Building2 size={20} />
            </div>
            <div>
              <h2 className="edit-org-title">{t('organizationDetail.editDetails.title')}</h2>
              <p className="edit-org-subtitle">{t('organizationDetail.editDetails.subtitle')}</p>
            </div>
          </div>
          <button className="edit-org-close" onClick={onClose} disabled={isLoading}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-org-form">
          <div className="form-group">
            <label>{t('organizationDetail.editDetails.fields.name')}</label>
            <input
              ref={firstInputRef}
              type="text"
              value={formData.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder={t('organizationDetail.editDetails.placeholders.name')}
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('organizationDetail.fields.legalName')}</label>
            <input
              type="text"
              value={formData.legalName || ''}
              onChange={(e) => updateField('legalName', e.target.value)}
              placeholder={t('organizationDetail.editDetails.placeholders.legalName')}
              disabled={isLoading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('organizationDetail.fields.taxId')}</label>
              <input
                type="text"
                value={formData.taxId || ''}
                onChange={(e) => updateField('taxId', e.target.value)}
                placeholder={t('organizationDetail.editDetails.placeholders.taxId')}
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label>{t('organizationDetail.fields.rccm')}</label>
              <input
                type="text"
                value={formData.rccm || ''}
                onChange={(e) => updateField('rccm', e.target.value)}
                placeholder={t('organizationDetail.editDetails.placeholders.rccm')}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('organizationDetail.fields.industry')}</label>
            <select
              value={formData.industry || ''}
              onChange={(e) => updateField('industry', e.target.value)}
              disabled={isLoading}
            >
              <option value="">{t('createOrganization.industry.select')}</option>
              {industries.map(ind => (
                <option key={ind.value} value={ind.value}>{ind.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>
              <Mail size={14} />
              {t('organizationDetail.fields.adminEmail')}
            </label>
            <input
              type="email"
              value={formData.primaryContactEmail || ''}
              onChange={(e) => updateField('primaryContactEmail', e.target.value)}
              placeholder={t('organizationDetail.editDetails.placeholders.email')}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <PhoneInput
              label={t('organizationDetail.fields.phone')}
              mask={editOrgCountryConfig.phonePlaceholder}
              countryCode={editOrgCountryCode}
              value={formData.primaryContactPhone || ''}
              onChange={(v) => updateField('primaryContactPhone', v)}
              name="primaryContactPhone"
            />
          </div>

          <div className="edit-org-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={isLoading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={isLoading || !formData.name?.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Edit size={16} />
                  {t('common.save')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      <style>{editOrgModalStyles}</style>
    </>
  );
};

const editOrgModalStyles = `
  .edit-org-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    z-index: 9998;
    animation: fadeIn 0.2s ease-out;
  }
  .edit-org-backdrop.exiting { animation: fadeOut 0.2s ease-out forwards; }

  .edit-org-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 16px;
    z-index: 9999;
    overflow: hidden;
    animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    display: flex;
    flex-direction: column;
  }
  .edit-org-modal.exiting { animation: slideOut 0.2s ease-in forwards; }

  .edit-org-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
  }
  .edit-org-header-content { display: flex; align-items: center; gap: 12px; }
  .edit-org-icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(239, 68, 68, 0.1));
    border: 1px solid rgba(239, 68, 68, 0.3);
    display: flex; align-items: center; justify-content: center;
    color: #EF4444;
  }
  .edit-org-title { font-size: 16px; font-weight: 600; color: var(--color-text-primary); margin: 0; }
  .edit-org-subtitle { font-size: 12px; color: var(--color-text-muted); margin: 2px 0 0; }
  .edit-org-close {
    width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
  }
  .edit-org-close:hover { background: var(--color-bg-hover); color: var(--color-text-primary); }
  .edit-org-close:disabled { opacity: 0.5; cursor: not-allowed; }

  .edit-org-form {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    flex: 1;
  }
  .edit-org-form .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .edit-org-form .form-group { display: flex; flex-direction: column; gap: 6px; }
  .edit-org-form .form-group label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.05em; color: var(--color-text-muted);
    display: flex; align-items: center; gap: 6px;
  }
  .edit-org-form .form-group input, .edit-org-form .form-group select {
    padding: 10px 12px; border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-size: 14px; transition: all 0.15s;
  }
  .edit-org-form .form-group input:focus, .edit-org-form .form-group select:focus {
    outline: none;
    border-color: #EF4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
  }
  .edit-org-form .form-group input:disabled, .edit-org-form .form-group select:disabled {
    opacity: 0.6; cursor: not-allowed;
  }
  .edit-org-form .form-group input::placeholder { color: var(--color-text-muted); }

  .edit-org-actions {
    display: flex; gap: 10px; padding-top: 8px;
    border-top: 1px solid var(--color-border-subtle);
    margin-top: 4px;
  }
  .edit-org-actions button {
    flex: 1; padding: 12px 16px; border-radius: 10px;
    font-size: 14px; font-weight: 600;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    cursor: pointer; transition: all 0.15s; border: none;
  }
  .edit-org-actions .btn-cancel {
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-default) !important;
  }
  .edit-org-actions .btn-cancel:hover:not(:disabled) { background: var(--color-bg-hover); color: var(--color-text-primary); }
  .edit-org-actions .btn-save {
    background: linear-gradient(135deg, #EF4444, #DC2626);
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }
  .edit-org-actions .btn-save:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4); }
  .edit-org-actions .btn-save:disabled, .edit-org-actions .btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }

  @media (max-width: 480px) {
    .edit-org-modal { width: 95%; border-radius: 12px; }
    .edit-org-form .form-row { grid-template-columns: 1fr; }
    .edit-org-actions { flex-direction: column-reverse; }
  }
`;

// Extend Subscription Modal Component
interface ExtendSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (days: number) => void;
  isLoading: boolean;
  currentPeriodEnd?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

const ExtendSubscriptionModal: React.FC<ExtendSubscriptionModalProps> = ({ isOpen, onClose, onSubmit, isLoading, currentPeriodEnd, t }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [days, setDays] = useState(30);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsExiting(false);
      setDays(30);
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setIsExiting(true);
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  if (!isVisible) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (days > 0) {
      onSubmit(days);
    }
  };

  const presetDays = [7, 14, 30, 60, 90];

  return (
    <>
      <div className={`extend-sub-backdrop ${isExiting ? 'exiting' : ''}`} onClick={() => !isLoading && onClose()} />
      <div className={`extend-sub-modal ${isExiting ? 'exiting' : ''}`}>
        <div className="extend-sub-header">
          <div className="extend-sub-header-content">
            <div className="extend-sub-icon">
              <Calendar size={20} />
            </div>
            <div>
              <h2 className="extend-sub-title">{t('organizationDetail.subscription.dialogs.extend.title')}</h2>
              <p className="extend-sub-subtitle">{t('organizationDetail.subscription.dialogs.extend.subtitle')}</p>
            </div>
          </div>
          <button className="extend-sub-close" onClick={onClose} disabled={isLoading}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="extend-sub-form">
          {currentPeriodEnd && (
            <div className="extend-sub-info">
              <span className="text-secondary text-sm">{t('organizationDetail.subscription.dialogs.extend.currentEnd')}:</span>
              <span className="font-medium text-foreground">{currentPeriodEnd}</span>
            </div>
          )}

          <div className="form-group">
            <label>{t('organizationDetail.subscription.dialogs.extend.daysLabel')}</label>
            <input
              ref={inputRef}
              type="number"
              min="1"
              max="365"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 0)}
              disabled={isLoading}
              required
            />
          </div>

          <div className="extend-sub-presets">
            <span className="text-xs text-secondary uppercase tracking-wide">{t('organizationDetail.subscription.dialogs.extend.quickSelect')}:</span>
            <div className="preset-buttons">
              {presetDays.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`preset-btn ${days === d ? 'active' : ''}`}
                  onClick={() => setDays(d)}
                  disabled={isLoading}
                >
                  {d} {t('common.days')}
                </button>
              ))}
            </div>
          </div>

          <div className="extend-sub-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={isLoading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-extend" disabled={isLoading || days <= 0}>
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  {t('organizationDetail.subscription.dialogs.extend.extending')}
                </>
              ) : (
                <>
                  <Calendar size={16} />
                  {t('organizationDetail.subscription.dialogs.extend.confirm', { days })}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      <style>{extendSubModalStyles}</style>
    </>
  );
};

const extendSubModalStyles = `
  .extend-sub-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    z-index: 9998;
    animation: fadeIn 0.2s ease-out;
  }
  .extend-sub-backdrop.exiting { animation: fadeOut 0.2s ease-out forwards; }

  .extend-sub-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 420px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 16px;
    z-index: 9999;
    overflow: hidden;
    animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .extend-sub-modal.exiting { animation: slideOut 0.2s ease-in forwards; }

  .extend-sub-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .extend-sub-header-content { display: flex; align-items: center; gap: 12px; }
  .extend-sub-icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
    border: 1px solid rgba(59, 130, 246, 0.3);
    display: flex; align-items: center; justify-content: center;
    color: #3B82F6;
  }
  .extend-sub-title { font-size: 16px; font-weight: 600; color: var(--color-text-primary); margin: 0; }
  .extend-sub-subtitle { font-size: 12px; color: var(--color-text-muted); margin: 2px 0 0; }
  .extend-sub-close {
    width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
  }
  .extend-sub-close:hover { background: var(--color-bg-hover); color: var(--color-text-primary); }
  .extend-sub-close:disabled { opacity: 0.5; cursor: not-allowed; }

  .extend-sub-form { padding: 20px; display: flex; flex-direction: column; gap: 16px; }

  .extend-sub-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: var(--color-bg-primary);
    border-radius: 8px;
    border: 1px solid var(--color-border-subtle);
  }

  .extend-sub-form .form-group { display: flex; flex-direction: column; gap: 6px; }
  .extend-sub-form .form-group label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.05em; color: var(--color-text-muted);
  }
  .extend-sub-form .form-group input {
    padding: 10px 12px; border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-size: 14px; transition: all 0.15s;
  }
  .extend-sub-form .form-group input:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
  .extend-sub-form .form-group input:disabled { opacity: 0.6; cursor: not-allowed; }

  .extend-sub-presets {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .preset-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .preset-btn {
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }
  .preset-btn:hover:not(:disabled) {
    background: var(--color-bg-hover);
    border-color: #3B82F6;
    color: #3B82F6;
  }
  .preset-btn.active {
    background: rgba(59, 130, 246, 0.15);
    border-color: #3B82F6;
    color: #3B82F6;
  }
  .preset-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .extend-sub-actions {
    display: flex; gap: 10px; padding-top: 8px;
    border-top: 1px solid var(--color-border-subtle);
    margin-top: 4px;
  }
  .extend-sub-actions button {
    flex: 1; padding: 12px 16px; border-radius: 10px;
    font-size: 14px; font-weight: 600;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    cursor: pointer; transition: all 0.15s; border: none;
  }
  .extend-sub-actions .btn-cancel {
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-default) !important;
  }
  .extend-sub-actions .btn-cancel:hover:not(:disabled) { background: var(--color-bg-hover); color: var(--color-text-primary); }
  .extend-sub-actions .btn-extend {
    background: linear-gradient(135deg, #3B82F6, #2563EB);
    color: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  .extend-sub-actions .btn-extend:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4); }
  .extend-sub-actions .btn-extend:disabled, .extend-sub-actions .btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }

  @media (max-width: 480px) {
    .extend-sub-modal { width: 95%; border-radius: 12px; }
    .extend-sub-actions { flex-direction: column-reverse; }
  }
`;

const subscriptionButtonStyles = `
  .manage-subscription-btn {
    background: linear-gradient(135deg, #8B5CF6 0%, #6366F1 50%, #4F46E5 100%);
    color: white;
    box-shadow:
      0 4px 14px rgba(99, 102, 241, 0.35),
      0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    position: relative;
    overflow: hidden;
  }

  .manage-subscription-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  .manage-subscription-btn:hover {
    transform: translateY(-2px);
    box-shadow:
      0 8px 20px rgba(99, 102, 241, 0.45),
      0 0 0 1px rgba(255, 255, 255, 0.15) inset,
      0 0 30px rgba(139, 92, 246, 0.3);
  }

  .manage-subscription-btn:hover::before {
    left: 100%;
  }

  .manage-subscription-btn:active {
    transform: translateY(0);
  }

  /* Pulse animation for attention */
  @keyframes subtlePulse {
    0%, 100% { box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1) inset; }
    50% { box-shadow: 0 4px 20px rgba(99, 102, 241, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.15) inset; }
  }

  .manage-subscription-btn {
    animation: subtlePulse 3s ease-in-out infinite;
  }

  .manage-subscription-btn:hover {
    animation: none;
  }
`;

const createUserModalStyles = `
  .create-user-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    z-index: 9998;
    animation: fadeIn 0.2s ease-out;
  }
  .create-user-backdrop.exiting { animation: fadeOut 0.2s ease-out forwards; }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }

  .create-user-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 440px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 16px;
    z-index: 9999;
    overflow: hidden;
    animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .create-user-modal.exiting { animation: slideOut 0.2s ease-in forwards; }
  @keyframes slideIn { from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
  @keyframes slideOut { from { opacity: 1; transform: translate(-50%, -50%) scale(1); } to { opacity: 0; transform: translate(-50%, -52%) scale(0.96); } }

  .create-user-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .create-user-header-content { display: flex; align-items: center; gap: 12px; }
  .create-user-icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
    border: 1px solid rgba(59, 130, 246, 0.3);
    display: flex; align-items: center; justify-content: center;
    color: #3B82F6;
  }
  .create-user-title { font-size: 16px; font-weight: 600; color: var(--color-text-primary); margin: 0; }
  .create-user-subtitle { font-size: 12px; color: var(--color-text-muted); margin: 2px 0 0; }
  .create-user-close {
    width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
  }
  .create-user-close:hover { background: var(--color-bg-hover); color: var(--color-text-primary); }
  .create-user-close:disabled { opacity: 0.5; cursor: not-allowed; }

  .create-user-form { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-group label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.05em; color: var(--color-text-muted);
    display: flex; align-items: center; gap: 6px;
  }
  .form-group input, .form-group select {
    padding: 10px 12px; border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-size: 14px; transition: all 0.15s;
  }
  .form-group input:focus, .form-group select:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
  .form-group input:disabled, .form-group select:disabled {
    opacity: 0.6; cursor: not-allowed;
  }
  .form-group input::placeholder { color: var(--color-text-muted); }

  .form-checkbox {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px;
    background: var(--color-bg-primary);
    border-radius: 8px;
    border: 1px solid var(--color-border-subtle);
  }
  .form-checkbox input[type="checkbox"] {
    width: 18px; height: 18px; accent-color: #3B82F6; cursor: pointer;
  }
  .form-checkbox label {
    font-size: 13px; color: var(--color-text-secondary); cursor: pointer;
  }

  .create-user-actions {
    display: flex; gap: 10px; padding-top: 8px;
    border-top: 1px solid var(--color-border-subtle);
    margin-top: 4px;
  }
  .create-user-actions button {
    flex: 1; padding: 12px 16px; border-radius: 10px;
    font-size: 14px; font-weight: 600;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    cursor: pointer; transition: all 0.15s; border: none;
  }
  .btn-cancel {
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-default) !important;
  }
  .btn-cancel:hover:not(:disabled) { background: var(--color-bg-hover); color: var(--color-text-primary); }
  .btn-create {
    background: linear-gradient(135deg, #3B82F6, #2563EB);
    color: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  .btn-create:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4); }
  .btn-create:disabled, .btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }

  @media (max-width: 480px) {
    .create-user-modal { width: 95%; border-radius: 12px; }
    .form-row { grid-template-columns: 1fr; }
    .create-user-actions { flex-direction: column-reverse; }
  }
`;

// Edit User Modal Component
interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateOrgUserRequest) => void;
  isLoading: boolean;
  user: OrganizationAdmin | null;
  orgCountry?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, onClose, onSubmit, isLoading, user, orgCountry, t }) => {
  const editUserCountryConfig = getCountryConfig(orgCountry || 'GN');
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [formData, setFormData] = useState<UpdateOrgUserRequest>({
    firstName: '',
    lastName: '',
    phone: '',
    role: 'USER',
  });
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && user) {
      setIsVisible(true);
      setIsExiting(false);
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        role: (user.role === 'ORG_ADMIN' || user.role === 'ADMIN') ? 'ADMIN' : 'USER',
      });
      setTimeout(() => firstInputRef.current?.focus(), 100);
    } else if (!isOpen) {
      setIsExiting(true);
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, user]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  if (!isVisible) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim()) return;
    onSubmit(formData);
  };

  const updateField = (field: keyof UpdateOrgUserRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div className={`edit-user-backdrop ${isExiting ? 'exiting' : ''}`} onClick={() => !isLoading && onClose()} />
      <div className={`edit-user-modal ${isExiting ? 'exiting' : ''}`}>
        <div className="edit-user-header">
          <div className="edit-user-header-content">
            <div className="edit-user-icon">
              <Pencil size={20} />
            </div>
            <div>
              <h2 className="edit-user-title">{t('organizationDetail.editUser.title')}</h2>
              <p className="edit-user-subtitle">{t('organizationDetail.editUser.subtitle')}</p>
            </div>
          </div>
          <button className="edit-user-close" onClick={onClose} disabled={isLoading}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit-user-form">
          <div className="form-row">
            <div className="form-group">
              <label>{t('organizationDetail.createUser.fields.firstName')}</label>
              <input
                ref={firstInputRef}
                type="text"
                value={formData.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                placeholder={t('organizationDetail.createUser.placeholders.firstName')}
                disabled={isLoading}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('organizationDetail.createUser.fields.lastName')}</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                placeholder={t('organizationDetail.createUser.placeholders.lastName')}
                disabled={isLoading}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>
              <Mail size={14} />
              {t('organizationDetail.createUser.fields.email')}
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="edit-user-readonly"
            />
          </div>

          <div className="form-group">
            <PhoneInput
              label={t('organizationDetail.createUser.fields.phone')}
              mask={editUserCountryConfig.phonePlaceholder}
              countryCode={orgCountry || 'GN'}
              value={formData.phone || ''}
              onChange={(v) => updateField('phone', v)}
              name="phone"
            />
          </div>

          <div className="form-group">
            <label>{t('organizationDetail.createUser.fields.role')}</label>
            <select
              value={formData.role}
              onChange={(e) => updateField('role', e.target.value)}
              disabled={isLoading || user?.role === 'ORG_ADMIN'}
            >
              <option value="USER">{t('organizationDetail.createUser.roles.user')}</option>
              <option value="ADMIN">{t('organizationDetail.createUser.roles.admin')}</option>
            </select>
            {user?.role === 'ORG_ADMIN' && (
              <span className="text-xs text-secondary mt-1" style={{ display: 'block', opacity: 0.7 }}>
                {t('organizationDetail.editUser.orgAdminLocked', 'Organization owner — role cannot be changed')}
              </span>
            )}
          </div>

          <div className="edit-user-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={isLoading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save-user" disabled={isLoading || !formData.firstName || !formData.lastName}>
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  {t('organizationDetail.editUser.saving')}
                </>
              ) : (
                <>
                  <Pencil size={16} />
                  {t('organizationDetail.editUser.save')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      <style>{editUserModalStyles}</style>
    </>
  );
};

const editUserModalStyles = `
  .edit-user-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    z-index: 9998;
    animation: fadeIn 0.2s ease-out;
  }
  .edit-user-backdrop.exiting { animation: fadeOut 0.2s ease-out forwards; }

  .edit-user-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 440px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 16px;
    z-index: 9999;
    overflow: hidden;
    animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .edit-user-modal.exiting { animation: slideOut 0.2s ease-in forwards; }

  .edit-user-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .edit-user-header-content { display: flex; align-items: center; gap: 12px; }
  .edit-user-icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(168, 85, 247, 0.1));
    border: 1px solid rgba(168, 85, 247, 0.3);
    display: flex; align-items: center; justify-content: center;
    color: #A855F7;
  }
  .edit-user-title { font-size: 16px; font-weight: 600; color: var(--color-text-primary); margin: 0; }
  .edit-user-subtitle { font-size: 12px; color: var(--color-text-muted); margin: 2px 0 0; }
  .edit-user-close {
    width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
  }
  .edit-user-close:hover { background: var(--color-bg-hover); color: var(--color-text-primary); }
  .edit-user-close:disabled { opacity: 0.5; cursor: not-allowed; }

  .edit-user-form { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
  .edit-user-form .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .edit-user-form .form-group { display: flex; flex-direction: column; gap: 6px; }
  .edit-user-form .form-group label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.05em; color: var(--color-text-muted);
    display: flex; align-items: center; gap: 6px;
  }
  .edit-user-form .form-group input, .edit-user-form .form-group select {
    padding: 10px 12px; border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-size: 14px; transition: all 0.15s;
  }
  .edit-user-form .form-group input:focus, .edit-user-form .form-group select:focus {
    outline: none;
    border-color: #A855F7;
    box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.15);
  }
  .edit-user-form .form-group input:disabled, .edit-user-form .form-group select:disabled {
    opacity: 0.6; cursor: not-allowed;
  }
  .edit-user-form .form-group input::placeholder { color: var(--color-text-muted); }

  .edit-user-readonly {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
    background: var(--color-bg-tertiary, var(--color-bg-primary)) !important;
  }

  .edit-user-actions {
    display: flex; gap: 10px; padding-top: 8px;
    border-top: 1px solid var(--color-border-subtle);
    margin-top: 4px;
  }
  .edit-user-actions button {
    flex: 1; padding: 12px 16px; border-radius: 10px;
    font-size: 14px; font-weight: 600;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    cursor: pointer; transition: all 0.15s; border: none;
  }
  .edit-user-actions .btn-cancel {
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-default) !important;
  }
  .edit-user-actions .btn-cancel:hover:not(:disabled) { background: var(--color-bg-hover); color: var(--color-text-primary); }
  .edit-user-actions .btn-save-user {
    background: linear-gradient(135deg, #A855F7, #9333EA);
    color: white;
    box-shadow: 0 4px 12px rgba(168, 85, 247, 0.3);
  }
  .edit-user-actions .btn-save-user:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(168, 85, 247, 0.4); }
  .edit-user-actions .btn-save-user:disabled, .edit-user-actions .btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }

  @media (max-width: 480px) {
    .edit-user-modal { width: 95%; border-radius: 12px; }
    .edit-user-form .form-row { grid-template-columns: 1fr; }
    .edit-user-actions { flex-direction: column-reverse; }
  }
`;

// Main Organization Detail Page
export default function OrganizationDetailPage() {
  const { t } = useTranslation('platform');
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error: showError } = useNotification();
  const { isDark } = useTheme();

  // Dialog states
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<OrganizationAdmin | null>(null);
  const [editDetailsModalOpen, setEditDetailsModalOpen] = useState(false);
  const [suspendSubDialogOpen, setSuspendSubDialogOpen] = useState(false);
  const [reactivateSubDialogOpen, setReactivateSubDialogOpen] = useState(false);
  const [extendSubModalOpen, setExtendSubModalOpen] = useState(false);

  // Invoice preview states
  const [previewInvoiceId, setPreviewInvoiceId] = useState<string | null>(null);
  const [printInvoiceId, setPrintInvoiceId] = useState<string | null>(null);

  // Fetch organization data from API
  const { data: org, isLoading, error, refetch } = useOrganization(id || '');

  // Mutation hooks for actions
  const suspendMutation = useSuspendOrganization();
  const reactivateMutation = useReactivateOrganization();
  const createUserMutation = useCreateOrgUser();
  const { data: orgRoles = [] } = useOrgRoles(createUserModalOpen ? id : undefined);
  const updateUserMutation = useUpdateOrgUser();
  const updateOrgMutation = useUpdateOrganization();
  const resendInviteMutation = useResendOrgUserInvite();
  const provisionRegisterMutation = useProvisionRegister();

  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [provisioningUserId, setProvisioningUserId] = useState<string | null>(null);
  const renderRegisterButton = (user: OrganizationAdmin) => {
    if (user.hasRegister) return null;
    return (
      <button
        onClick={() => {
          setProvisioningUserId(user.id);
          provisionRegisterMutation.mutate(
            { orgId: id!, userId: user.id },
            {
              onSuccess: () => {
                setProvisioningUserId(null);
                success('Cash register provisioned successfully');
              },
              onError: (err) => {
                setProvisioningUserId(null);
                showError(isApiError(err) ? formatErrorForDisplay(err).message : 'Failed to provision register');
              },
            }
          );
        }}
        disabled={provisioningUserId === user.id}
        className="px-2 py-1 text-xs font-medium rounded-md bg-orange-500/15 text-orange-500 hover:bg-orange-500/25 transition-colors disabled:opacity-50 flex items-center gap-1"
        title="Create cash register"
      >
        {provisioningUserId === user.id ? (
          <><Loader2 className="w-3 h-3 animate-spin" /> Creating...</>
        ) : (
          <><CreditCard className="w-3 h-3" /> Create Register</>
        )}
      </button>
    );
  };

  // Subscription hooks
  const { data: subscription, refetch: refetchSubscription } = useOrgSubscription(id || '');
  const suspendSubMutation = useSuspendSubscription();
  const reactivateSubMutation = useReactivateSubscription();
  const extendSubMutation = useExtendSubscription();
  const updateGracePeriodMutation = useUpdateGracePeriod();

  // Invoice hooks
  const { data: invoicesData } = useOrganizationInvoices(id || '', 0, 5); // Get latest 5 invoices
  const downloadPdfVariant = useDownloadInvoicePdfVariant();

  const handleSuspendConfirm = () => {
    if (!id) return;
    suspendMutation.mutate({ id }, {
      onSuccess: () => {
        setSuspendDialogOpen(false);
        refetch();
      },
      onError: () => {
        setSuspendDialogOpen(false);
      },
    });
  };

  const handleReactivateConfirm = () => {
    if (!id) return;
    reactivateMutation.mutate(id, {
      onSuccess: () => {
        setReactivateDialogOpen(false);
        refetch();
      },
      onError: () => {
        setReactivateDialogOpen(false);
      },
    });
  };

  const handleCreateUser = (data: CreateOrgUserRequest) => {
    if (!id) return;
    createUserMutation.mutate({ orgId: id, data }, {
      onSuccess: () => {
        success(t('organizationDetail.createUser.success'));
        setCreateUserModalOpen(false);
        refetch();
      },
      onError: (err) => {
        const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('organizationDetail.createUser.error') };
        showError(message);
      },
    });
  };

  const handleUpdateUser = (data: UpdateOrgUserRequest) => {
    if (!id || !editingUser) return;
    updateUserMutation.mutate({ orgId: id, userId: editingUser.id, data }, {
      onSuccess: () => {
        success(t('organizationDetail.editUser.success'));
        setEditUserModalOpen(false);
        setEditingUser(null);
        refetch();
      },
      onError: (err) => {
        const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('organizationDetail.editUser.error') };
        showError(message);
      },
    });
  };

  const handleUpdateOrgDetails = (data: UpdateOrganizationRequest) => {
    if (!id) return;
    updateOrgMutation.mutate({ id, data }, {
      onSuccess: () => {
        success(t('organizationDetail.editDetails.success'));
        setEditDetailsModalOpen(false);
        refetch();
      },
      onError: (err) => {
        const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('organizationDetail.editDetails.error') };
        showError(message);
      },
    });
  };

  // Subscription management handlers
  const handleSuspendSubscription = () => {
    if (!id) return;
    suspendSubMutation.mutate({ orgId: id, reason: 'Suspended by admin' }, {
      onSuccess: () => {
        success(t('organizationDetail.subscription.suspendSuccess'));
        setSuspendSubDialogOpen(false);
        refetch();
        refetchSubscription();
      },
      onError: (err) => {
        const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('organizationDetail.subscription.suspendError') };
        showError(message);
        setSuspendSubDialogOpen(false);
      },
    });
  };

  const handleReactivateSubscription = () => {
    if (!id) return;
    reactivateSubMutation.mutate(id, {
      onSuccess: () => {
        success(t('organizationDetail.subscription.reactivateSuccess'));
        setReactivateSubDialogOpen(false);
        refetch();
        refetchSubscription();
      },
      onError: (err) => {
        const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('organizationDetail.subscription.reactivateError') };
        showError(message);
        setReactivateSubDialogOpen(false);
      },
    });
  };

  const handleExtendSubscription = (days: number) => {
    if (!id || days <= 0) return;
    extendSubMutation.mutate({ orgId: id, days }, {
      onSuccess: () => {
        success(t('organizationDetail.subscription.extendSuccess', { days }));
        setExtendSubModalOpen(false);
        refetch();
        refetchSubscription();
      },
      onError: (err) => {
        const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('organizationDetail.subscription.extendError') };
        showError(message);
      },
    });
  };

  // Invoice handlers - use theme-aware variant (same as preview)
  const handleInvoiceDownload = (invoiceId: string, invoiceNumber?: string) => {
    const variant = isDark ? 'blackops' : 'tactical';
    downloadPdfVariant.mutate({
      id: invoiceId,
      variant,
      language: t('common.language') === 'fr' ? 'fr' : 'en',
      filename: invoiceNumber ? `${invoiceNumber}.pdf` : undefined,
    }, {
      onSuccess: () => {
        success(t('invoices.downloadSuccess'));
      },
      onError: (err) => {
        const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('common.error') };
        showError(message);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted">
        <Building2 className="w-12 h-12 mb-3 opacity-50" />
        <p>{error ? `${t('common.error')}: ${error.message}` : t('organizationDetail.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="org-detail-header flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/superadmin/organizations')}
              className="p-2 rounded-lg border border-border hover:bg-background transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center text-white text-sm md:text-lg font-bold flex-shrink-0">
              {org.name.substring(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg md:text-2xl font-bold text-foreground flex items-center gap-2 flex-wrap">
                <span className="truncate">{org.name}</span>
                <StatusBadge status={org.status} t={t} />
              </h1>
              <p className="text-sm md:text-base text-secondary truncate">{org.code} • {org.primaryContactEmail}</p>
            </div>
          </div>
          <div className="org-detail-actions flex items-center gap-2 flex-shrink-0">
            {org.status === 'SUSPENDED' ? (
              <button
                onClick={() => setReactivateDialogOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
              >
                <Play className="w-4 h-4" /> <span className="hidden sm:inline">{t('organizationDetail.actions.reactivate')}</span>
              </button>
            ) : (org.status === 'ACTIVE' || org.status === 'TRIAL') && (
              <button
                onClick={() => setSuspendDialogOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
              >
                <Pause className="w-4 h-4" /> <span className="hidden sm:inline">{t('organizationDetail.actions.suspend')}</span>
              </button>
            )}
            <button
              onClick={() => navigate(`/superadmin/organizations/${id}/edit`)}
              className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
            >
              <Edit className="w-4 h-4" /> <span className="hidden sm:inline">{t('common.edit')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Payment Alert Banner */}
      {subscription && (
        <PaymentAlertBanner
          subscription={{
            ...subscription,
            nextBillingDate: subscription.nextBillingDate || subscription.currentPeriodEnd,
          }}
          orgId={id || ''}
        />
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Users className="w-4 h-4" />}
          value={org.currentUserCount ?? 0}
          label={t('organizationDetail.stats.activeUsers')}
          color="green"
        />
        <StatCard
          icon={<DollarSign className="w-4 h-4" />}
          value={formatCurrency(org.monthlyPrice ?? 0, 'USD')}
          label={t('organizationDetail.stats.monthlyRevenue') + ' (USD)'}
          color="blue"
          secondaryValue={org.currency !== 'USD' && org.monthlyPriceLocal && org.monthlyPriceLocal > 0 ? formatCurrency(org.monthlyPriceLocal, org.currency) : undefined}
          secondaryLabel={org.currency !== 'USD' && org.monthlyPriceLocal && org.monthlyPriceLocal > 0 ? `${t('organizationDetail.stats.localCurrency')} (${org.currency})` : undefined}
        />
        <StatCard
          icon={<Database className="w-4 h-4" />}
          value={`${org.storageUsedGb ?? 0} GB`}
          label={t('organizationDetail.stats.storageUsed')}
          color="purple"
        />
        <StatCard
          icon={<Activity className="w-4 h-4" />}
          value={(org.apiCallsThisMonth ?? 0).toLocaleString()}
          label={t('organizationDetail.stats.apiCalls')}
          color="orange"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organization Details */}
        <div className="bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-md font-semibold text-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4" /> {t('organizationDetail.sections.details')}
            </h3>
            <button
              onClick={() => setEditDetailsModalOpen(true)}
              className="flex items-center gap-1.5 text-sm text-secondary hover:text-foreground transition-colors"
            >
              <Edit className="w-3.5 h-3.5" />
              {t('common.edit')}
            </button>
          </div>
          <div className="flex flex-col gap-3">
            <DetailItem label={t('organizationDetail.fields.legalName')} value={org.legalName || '-'} />
            <DetailItem label={t('organizationDetail.fields.taxId')} value={<span className="font-mono">{org.taxId || '-'}</span>} />
            <DetailItem label={t('organizationDetail.fields.rccm')} value={<span className="font-mono">{org.rccm || '-'}</span>} />
            <DetailItem label={t('organizationDetail.fields.industry')} value={org.industry || '-'} />
            <DetailItem label={t('organizationDetail.fields.adminEmail')} value={org.primaryContactEmail || '-'} />
            <DetailItem label={t('organizationDetail.fields.phone')} value={org.primaryContactPhone || '-'} />
          </div>
        </div>

        {/* Subscription Details */}
        <div className="bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-md font-semibold text-foreground flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> {t('organizationDetail.sections.subscription')}
            </h3>
            <div className="flex items-center gap-2">
              {/* Subscription Status Actions */}
              {subscription?.status === 'SUSPENDED' ? (
                <button
                  onClick={() => setReactivateSubDialogOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors"
                  title={t('organizationDetail.subscription.reactivate')}
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t('organizationDetail.subscription.reactivate')}</span>
                </button>
              ) : subscription && (
                <button
                  onClick={() => setSuspendSubDialogOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 transition-colors"
                  title={t('organizationDetail.subscription.suspend')}
                >
                  <Ban className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t('organizationDetail.subscription.suspend')}</span>
                </button>
              )}
              {subscription && (
                <button
                  onClick={() => setExtendSubModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                  title={t('organizationDetail.subscription.extend')}
                >
                  <Calendar className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t('organizationDetail.subscription.extend')}</span>
                </button>
              )}
              <button
                onClick={() => navigate(`/superadmin/organizations/${id}/subscription`)}
                className="manage-subscription-btn group flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
              >
                <Sparkles className="w-4 h-4" />
                <span>{t('organizationDetail.actions.changePlan')}</span>
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <DetailItem label={t('organizationDetail.fields.currentPlan')} value={<PlanBadge tier={org.subscriptionTier} />} />
            <DetailItem label={t('organizationDetail.fields.status')} value={<StatusBadge status={org.status} t={t} />} />
            <DetailItem label={t('organizationDetail.fields.billingCycle')} value={org.billingCycle || '-'} />
            <DetailItem label={t('organizationDetail.fields.currency')} value={org.currency || 'USD'} />
            <DetailItem label={t('organizationDetail.fields.nextRenewal')} value={org.nextBillingDate || '-'} />
            <DetailItem label={t('organizationDetail.fields.paymentMethod')} value={org.paymentMethod || '-'} />
            <DetailItem label={t('organizationDetail.fields.referredBy')} value={org.referredByPartnerId ? `${t('organizationDetail.fields.partner')} (${org.partnerCommissionRate ?? 0}%)` : t('organizationDetail.fields.direct')} />
            <DetailItem
              label={t('organizationDetail.fields.gracePeriod')}
              value={
                <div className="flex items-center gap-2">
                  <span>{subscription?.gracePeriodDays ?? 7} {t('organizationDetail.fields.days')}</span>
                  <button
                    onClick={() => {
                      const newDays = prompt(t('organizationDetail.subscription.gracePeriodPrompt'), String(subscription?.gracePeriodDays ?? 7));
                      if (newDays && !isNaN(Number(newDays)) && Number(newDays) >= 1 && Number(newDays) <= 90) {
                        updateGracePeriodMutation.mutate({ orgId: id!, days: Number(newDays) }, {
                          onSuccess: () => {
                            success(t('organizationDetail.subscription.gracePeriodUpdateSuccess'));
                          },
                          onError: (err) => {
                            const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('organizationDetail.subscription.gracePeriodUpdateError') };
                            showError(message);
                          },
                        });
                      }
                    }}
                    className="text-xs hover:opacity-70 transition-opacity"
                    title={t('organizationDetail.subscription.editGracePeriod')}
                    style={{ color: 'var(--color-brand-primary)' }}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                </div>
              }
            />
          </div>
        </div>
      </div>

      {/* Recent Invoices */}
      {invoicesData && invoicesData.content && invoicesData.content.length > 0 && (
        <div className="bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-md font-semibold text-foreground flex items-center gap-2">
              <FileText className="w-4 h-4" /> {t('organizationDetail.sections.recentInvoices')}
            </h3>
            <button
              onClick={() => navigate(`/superadmin/invoices?orgId=${id}`)}
              className="text-sm text-primary hover:underline"
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="space-y-2">
            {invoicesData.content.map((invoice) => (
              <div
                key={invoice.id}
                className="flex items-center justify-between py-3 px-4 bg-background hover:bg-hover rounded-lg transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <FileText className="w-4 h-4 text-muted flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => setPreviewInvoiceId(invoice.id)}
                      className="font-mono text-sm font-semibold text-primary hover:underline"
                      title={t('invoices.clickToPreview')}
                    >
                      {invoice.invoiceNumber}
                    </button>
                    <div className="text-xs text-muted mt-0.5">
                      {invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString() : '-'}
                      {invoice.dueDate && ` • ${t('invoices.due')}: ${new Date(invoice.dueDate).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-semibold text-foreground">
                      {formatCurrency(invoice.totalUsd, 'USD')}
                    </div>
                    {invoice.totalLocal && invoice.billingCurrency && invoice.billingCurrency !== 'USD' && (
                      <div className="font-mono text-xs text-muted">
                        {formatCurrency(invoice.totalLocal, invoice.billingCurrency)}
                      </div>
                    )}
                    <div className={`text-xs font-medium mt-0.5 ${
                      invoice.status === 'PAID' ? 'text-emerald-500' :
                      invoice.status === 'OVERDUE' ? 'text-red-500' :
                      invoice.status === 'ISSUED' ? 'text-blue-500' :
                      'text-muted'
                    }`}>
                      {invoice.status}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleInvoiceDownload(invoice.id, invoice.invoiceNumber)}
                    className="p-2 hover:bg-surface rounded-lg transition-colors"
                    title={t('common.download')}
                  >
                    <Download className="w-4 h-4 text-muted hover:text-foreground" />
                  </button>
                  <button
                    onClick={() => setPrintInvoiceId(invoice.id)}
                    className="p-2 hover:bg-surface rounded-lg transition-colors"
                    title={t('common.print')}
                  >
                    <Printer className="w-4 h-4 text-muted hover:text-foreground" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Users */}
      <div className="bg-surface border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-md font-semibold text-foreground flex items-center gap-2">
            <Shield className="w-4 h-4" /> {t('organizationDetail.sections.adminUsers')}
          </h3>
          <button
            onClick={() => setCreateUserModalOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('organizationDetail.addUser')}</span>
          </button>
        </div>
        <div className="space-y-2">
          {org.adminUsers && org.adminUsers.length > 0 ? (
            org.adminUsers.map((admin) => (
              <div key={admin.id} className="flex items-center justify-between py-2 px-3 bg-background rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                    {admin.name ? admin.name.split(' ').map(n => n[0]).join('') : '?'}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{admin.name || t('organizationDetail.unknown')}</div>
                    <div className="text-xs text-secondary">{admin.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {renderRegisterButton(admin)}
                  <button
                    onClick={() => {
                      setEditingUser(admin);
                      setEditUserModalOpen(true);
                    }}
                    className="p-1.5 rounded-md hover:bg-purple-500/15 text-secondary hover:text-purple-500 transition-colors"
                    title={t('organizationDetail.editUser.title')}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {admin.status === 'PENDING_VERIFICATION' && (
                    <button
                      onClick={() => {
                        setResendingUserId(admin.id);
                        resendInviteMutation.mutate(
                          { orgId: id!, userId: admin.id },
                          {
                            onSuccess: () => {
                              setResendingUserId(null);
                              success(t('organizationDetail.resendInviteSuccess'));
                            },
                            onError: () => {
                              setResendingUserId(null);
                              showError(t('organizationDetail.resendInviteError'));
                            },
                          }
                        );
                      }}
                      disabled={resendingUserId === admin.id}
                      className="px-2 py-1 text-xs font-medium rounded-md bg-blue-500/15 text-blue-500 hover:bg-blue-500/25 transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                      {resendingUserId === admin.id ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> {t('organizationDetail.resendingInvite')}</>
                      ) : (
                        <><Mail className="w-3 h-3" /> {t('organizationDetail.resendInvite')}</>
                      )}
                    </button>
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    admin.status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-500' :
                    admin.status === 'PENDING_VERIFICATION' ? 'bg-amber-500/15 text-amber-500' :
                    'bg-gray-500/15 text-gray-500'
                  }`}>
                    {admin.status}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-muted py-4 text-center">{t('organizationDetail.noAdminUsers')}</div>
          )}
        </div>
      </div>

      {/* Other Users (managers, cashiers, ...) — cash register provisioning */}
      <div className="bg-surface border border-border rounded-xl p-5">
        <h3 className="text-md font-semibold text-foreground flex items-center gap-2 mb-4">
          <Users className="w-4 h-4" /> {t('organizationDetail.sections.otherUsers')}
        </h3>
        <div className="space-y-2">
          {org.otherUsers && org.otherUsers.length > 0 ? (
            org.otherUsers.map((user) => (
              <div key={user.id} className="flex items-center justify-between py-2 px-3 bg-background rounded-lg">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {user.name ? user.name.split(' ').map(n => n[0]).join('') : '?'}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{user.name || t('organizationDetail.unknown')}</div>
                    <div className="text-xs text-secondary truncate">{user.email} · {user.role}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {renderRegisterButton(user)}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    user.status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-500' :
                    user.status === 'PENDING_VERIFICATION' ? 'bg-amber-500/15 text-amber-500' :
                    'bg-gray-500/15 text-gray-500'
                  }`}>
                    {user.status}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-muted py-4 text-center">{t('organizationDetail.noOtherUsers')}</div>
          )}
        </div>
      </div>

      {/* Premium Invoice Templates */}
      <PremiumTemplatesCard orgId={id!} />

      {/* Organization Settings */}
      <OrganizationSettingsTab orgId={id!} orgName={org.name} />

      {/* Tenant Data Management (backup / purge / restore) */}
      <TenantDataCard orgId={id!} orgName={org.name} />

      {/* Suspend Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={suspendDialogOpen}
        variant="warning"
        title={t('organizationDetail.dialogs.suspend.title')}
        message={t('organizationDetail.dialogs.suspend.message')}
        details={org && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('organizationDetail.dialogs.organization')}:</span>
              <span className="font-medium">{org.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('organizationDetail.fields.status')}:</span>
              <StatusBadge status={org.status} t={t} />
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('organizationDetail.stats.activeUsers')}:</span>
              <span className="font-medium">{org.currentUserCount ?? 0}</span>
            </div>
          </div>
        )}
        confirmText={t('organizationDetail.actions.suspend')}
        isLoading={suspendMutation.isPending}
        onConfirm={handleSuspendConfirm}
        onCancel={() => setSuspendDialogOpen(false)}
      />

      {/* Reactivate Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={reactivateDialogOpen}
        variant="approve"
        title={t('organizationDetail.dialogs.reactivate.title')}
        message={t('organizationDetail.dialogs.reactivate.message')}
        details={org && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('organizationDetail.dialogs.organization')}:</span>
              <span className="font-medium">{org.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('organizationDetail.dialogs.plan')}:</span>
              <PlanBadge tier={org.subscriptionTier} />
            </div>
          </div>
        )}
        confirmText={t('organizationDetail.actions.reactivate')}
        isLoading={reactivateMutation.isPending}
        onConfirm={handleReactivateConfirm}
        onCancel={() => setReactivateDialogOpen(false)}
      />

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={createUserModalOpen}
        onClose={() => setCreateUserModalOpen(false)}
        onSubmit={handleCreateUser}
        isLoading={createUserMutation.isPending}
        roles={orgRoles}
        orgCountry={getCountryByName(org?.country || '')?.code || 'GN'}
        t={t}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={editUserModalOpen}
        onClose={() => { setEditUserModalOpen(false); setEditingUser(null); }}
        onSubmit={handleUpdateUser}
        isLoading={updateUserMutation.isPending}
        user={editingUser}
        orgCountry={getCountryByName(org?.country || '')?.code || 'GN'}
        t={t}
      />

      {/* Edit Organization Details Modal */}
      <EditOrgDetailsModal
        isOpen={editDetailsModalOpen}
        onClose={() => setEditDetailsModalOpen(false)}
        onSubmit={handleUpdateOrgDetails}
        isLoading={updateOrgMutation.isPending}
        initialData={org}
        t={t}
      />

      {/* Suspend Subscription Dialog */}
      <ConfirmationDialog
        isOpen={suspendSubDialogOpen}
        variant="warning"
        title={t('organizationDetail.subscription.dialogs.suspend.title')}
        message={t('organizationDetail.subscription.dialogs.suspend.message')}
        details={subscription && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('organizationDetail.subscription.dialogs.currentStatus')}:</span>
              <StatusBadge status={subscription.status} t={t} />
            </div>
            <div className="flex justify-between">
              <span className="text-muted">{t('organizationDetail.subscription.dialogs.periodEnd')}:</span>
              <span className="font-medium">{subscription.currentPeriodEnd || '-'}</span>
            </div>
          </div>
        )}
        confirmText={t('organizationDetail.subscription.suspend')}
        isLoading={suspendSubMutation.isPending}
        onConfirm={handleSuspendSubscription}
        onCancel={() => setSuspendSubDialogOpen(false)}
      />

      {/* Reactivate Subscription Dialog */}
      <ConfirmationDialog
        isOpen={reactivateSubDialogOpen}
        variant="approve"
        title={t('organizationDetail.subscription.dialogs.reactivate.title')}
        message={t('organizationDetail.subscription.dialogs.reactivate.message')}
        details={subscription && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted">{t('organizationDetail.subscription.dialogs.currentStatus')}:</span>
              <StatusBadge status={subscription.status} t={t} />
            </div>
          </div>
        )}
        confirmText={t('organizationDetail.subscription.reactivate')}
        isLoading={reactivateSubMutation.isPending}
        onConfirm={handleReactivateSubscription}
        onCancel={() => setReactivateSubDialogOpen(false)}
      />

      {/* Extend Subscription Modal */}
      <ExtendSubscriptionModal
        isOpen={extendSubModalOpen}
        onClose={() => setExtendSubModalOpen(false)}
        onSubmit={handleExtendSubscription}
        isLoading={extendSubMutation.isPending}
        currentPeriodEnd={subscription?.currentPeriodEnd}
        t={t}
      />

      {/* Invoice Preview Modal - Theme-aware (dark: blackops, light: tactical) */}
      {previewInvoiceId && (
        <InvoicePreviewModal
          invoiceId={previewInvoiceId}
          isOpen={!!previewInvoiceId}
          onClose={() => setPreviewInvoiceId(null)}
          onPrint={() => {
            setPrintInvoiceId(previewInvoiceId);
            setPreviewInvoiceId(null);
          }}
        />
      )}

      {/* Print Invoice Dialog */}
      {printInvoiceId && (
        <PrintInvoiceDialog
          invoiceId={printInvoiceId}
          isOpen={!!printInvoiceId}
          onClose={() => setPrintInvoiceId(null)}
        />
      )}

      {/* Subscription Button Styles */}
      <style>{subscriptionButtonStyles}</style>
    </div>
  );
}
